import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import React, { useContext, useEffect, useRef, useState } from 'react'
import SettingsFormContainer from 'src/Elements/SystemSettings/forms/SettingsFormContainer'
import FullVersionFormContent from './form-content/FullVersionFormContent'
import { DynamicPricesSettings } from '../../types'
import { makeStyles } from 'tss-react/mui'
import FormikWithExitHandler from 'src/Libs/form/components/FormikWithExitHandler'
import { useWallboxes } from 'src/Libs/wallboxes'
import { WallboxType } from 'src/Libs/wallboxes/dto'
import SunModeDisabledDialog from './SunModeDisabledDialog'
import { useScrollToHash } from 'src/Libs/navigation/hooks'
import { SystemSettingHashes } from 'src/Libs/navigation/types'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import { COUNTRY_NOT_AVAILABLE_KEY, MASTER_IS_OFFLINE_KEY } from '../../const'
import { useLocation, useNavigate } from 'react-router-dom'
import { FormBlankView, FormOptIn } from './form-content'
import { usePrevious } from '@e3dc-react/shell/Hooks'
import { Box } from '@mui/material'
import { useNewRelic } from '@e3dc-react/shell/Libs/observability-tool'
import { useCheckInstallationLocation, useDPPSettingsFormFullFormInitializer, useIsDynamicEmsAccessible } from '../../hooks'
import ConfirmFormSubmitDialog from './ConfirmFormSubmitDialog'
import { useFarmContext } from 'src/Elements/Farm'

const useStyles = makeStyles()(theme => ({
  contentAccordion: {
    padding: theme.spacing(0),
  },
  headerContent: {
    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(4),
    },
  },
}))

interface Props {
  installationId?: string
  storageId?: string
  dynamicPricesSettings: DynamicPricesSettings | null
  handleDynamicPricesSettingsUpdate: (newValues: DynamicPricesSettings) => void
  hasWritingRights?: boolean
  isOptInRequired?: boolean
}

/**
 * This component renders the dynamic prices section of the system settings
 */
const SettingsFormFullVersion: React.FC<Props> = ({
  installationId,
  dynamicPricesSettings,
  handleDynamicPricesSettingsUpdate,
  hasWritingRights = true,
  isOptInRequired = false,
  storageId,
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { pageActions } = useNewRelic()
  const { classes } = useStyles()
  const accordionRef = useRef<HTMLDivElement | null>(null)
  const pendingValuesRef = useRef<DynamicPricesSettings | null>(null)
  const { t } = useCustomTranslation()
  const { isLoading: wallboxesIsLoading, wallboxes, updateSettings } = useWallboxes()
  const [isSunModeDialogOpen, setIsSunModeDialogOpen] = useState(false)
  const [isConfirmToRedirectDialogOpen, setIsConfirmToRedirectDialogOpen] = useState(false)
  const [isConfirmDeactivationDialogOpen, setIsConfirmDeactivationDialogOpen] = useState(false)
  const { isDppCountryInstallation } = useCheckInstallationLocation()
  const isDynamicPricesSettingsAvailable = dynamicPricesSettings !== null
  const isAllowedInstallationLocation = isDppCountryInstallation || isDynamicPricesSettingsAvailable
  const { initialValues, validationSchema } = useDPPSettingsFormFullFormInitializer(dynamicPricesSettings)
  const { isDynamicEmsV2Accessible } = useIsDynamicEmsAccessible()

  const { isServerOnline } = useContext(InstallationOnlineStatusContext)
  const { isFarm } = useFarmContext()
  const isServerOffline = isFarm && isServerOnline !== true
  const previousIsOptInRequired = usePrevious(isOptInRequired)
  const [hasOptedInChanged, setHasOptedInChanged] = useState(false)
  useScrollToHash(accordionRef, SystemSettingHashes.DynamicPowerPriceSettings)

  /**
   * This effect is used to check if the opt-in status has changed.
   * If it has changed the user opted in and new form content is displayed.
   * The hasOptedInChanged state is used to keep the accordion open after the user has opted in.
   */
  useEffect(() => {
    if (previousIsOptInRequired !== isOptInRequired) {
      setHasOptedInChanged(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const nonBidiActiveSunModeDisabledWallboxes = wallboxes.filter(
    wallbox => wallbox.isAvailable && wallbox.wallboxType !== WallboxType.Bidi && wallbox.sunModeActive === false
  )
  const hasNoWallboxes = !wallboxesIsLoading && wallboxes.length === 0
  const isDppWallboxToggleDisabled = hasNoWallboxes && initialValues.dynamicPricesForWallboxesEnabled === false
  const isExpanded = (!isServerOffline && (isAllowedInstallationLocation || isOptInRequired)) || hasOptedInChanged

  const handleSubmit = (values: DynamicPricesSettings, initialValues?: DynamicPricesSettings): void => {
    const hasDynamicPowerPricesForWallboxesBeenEnabled =
      initialValues?.dynamicPricesForWallboxesEnabled === false && values.dynamicPricesForWallboxesEnabled

    setIsSunModeDialogOpen(hasDynamicPowerPricesForWallboxesBeenEnabled && nonBidiActiveSunModeDisabledWallboxes.length > 0)

    /**
     * AI360 2.0 cannot work at the same time when the price threshold is activated.
     * Shows a dialog to confirm deactivation of AI360 2.0 if the user selects 'No' or '360 Price Threshold' options for battery settings.
     */
    if (isDynamicEmsV2Accessible && initialValues?.isDynamicEmsEnabledForBattery && !values.isDynamicEmsEnabledForBattery) {
      pendingValuesRef.current = values
      setIsConfirmDeactivationDialogOpen(true)
      return
    }

    // Navigates user to AI360 2.0 page to continue the configuration.
    const hasEnabledAi360 = !initialValues?.isDynamicEmsEnabledForBattery && values.isDynamicEmsEnabledForBattery
    if (isDynamicEmsV2Accessible && hasEnabledAi360 && values.dynamicPricesForBatteryEnabled) {
      setIsConfirmToRedirectDialogOpen(true)
      return
    }

    handleDynamicPricesSettingsUpdate(values)
  }

  const handleConfirmRedirectDialog = (): void => {
    setIsConfirmToRedirectDialogOpen(false)
    navigate(`/dashboard/ai360/${installationId}/${storageId}`, { state: { shouldDisplaySettings: true } })
  }

  const handleConfirmDeactivationDialog = (): void => {
    setIsConfirmDeactivationDialogOpen(false)
    if (pendingValuesRef.current) {
      handleDynamicPricesSettingsUpdate(pendingValuesRef.current)
      pendingValuesRef.current = null
    }
  }

  if (isOptInRequired)
    return (
      <Box ref={accordionRef} sx={{ mb: 1 }}>
        <FormOptIn isServerOffline={isServerOffline} />
      </Box>
    )

  if (!isDynamicPricesSettingsAvailable)
    return <FormBlankView isAllowedInstallationLocation={isAllowedInstallationLocation} isServerOffline={isServerOffline} />

  const accordionTitle = t('systemSettings.price-based-charging.section-title')

  return (
    <FormikWithExitHandler
      formKey="dynamic-price-settings-full-version"
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      <OptionalTooltipWrapper
        hasTooltip={!isAllowedInstallationLocation || isServerOffline}
        placement={'top'}
        titleKey={isServerOffline ? MASTER_IS_OFFLINE_KEY : COUNTRY_NOT_AVAILABLE_KEY}
      >
        <AccordionItem
          ref={accordionRef}
          disabled={!isAllowedInstallationLocation || isServerOffline}
          title={accordionTitle}
          contentClassName={classes.contentAccordion}
          headerContentClassName={classes.headerContent}
          onClick={() => {
            const isWallboxPage = location.pathname.includes('wallbox')

            pageActions.componentClicked({ componentName: isWallboxPage ? 'WallboxDPPSettings' : 'SmartChargeDPPSettings' })
          }}
          expanded={isExpanded}
        >
          <SunModeDisabledDialog
            isOpen={isSunModeDialogOpen}
            nonBidiActiveSunModeDisabledWallboxes={nonBidiActiveSunModeDisabledWallboxes}
            handleClose={() => setIsSunModeDialogOpen(false)}
            updateSettings={updateSettings}
          />
          {isConfirmToRedirectDialogOpen && (
            <ConfirmFormSubmitDialog
              isOpen={isConfirmToRedirectDialogOpen}
              onClose={() => setIsConfirmToRedirectDialogOpen(false)}
              onConfirm={handleConfirmRedirectDialog}
              primaryActionText={t('common.next')}
              title={t('systemSettings.price-based-charging.confirm-redirect-title')}
              description={t('systemSettings.price-based-charging.confirm-redirect-description')}
            />
          )}
          {isConfirmDeactivationDialogOpen && (
            <ConfirmFormSubmitDialog
              isOpen={isConfirmDeactivationDialogOpen}
              onClose={() => setIsConfirmDeactivationDialogOpen(false)}
              onConfirm={handleConfirmDeactivationDialog}
              primaryActionText={t('common.deactivate')}
              title={t('systemSettings.price-based-charging.confirm-deactivation-title')}
              description={t('systemSettings.price-based-charging.confirm-deactivation-description')}
            />
          )}
          <SettingsFormContainer containerStyle={{ pt: '12px' }} model={initialValues} isLoading={wallboxesIsLoading}>
            <FullVersionFormContent
              installationId={installationId}
              isDppWallboxToggleDisabled={isDppWallboxToggleDisabled}
              hasWritingRights={hasWritingRights}
            />
          </SettingsFormContainer>
        </AccordionItem>
      </OptionalTooltipWrapper>
    </FormikWithExitHandler>
  )
}

export default SettingsFormFullVersion
